import React, {useEffect} from 'react';
import {useAsyncSsrManager, useLogger, useMemoryStore} from '../../context';
import {isInBrowser} from '../../utils/browser/isInBrowser';
import {useIsClientOnlyRendering} from '../../utils/useIsClientOnlyRendering';

export interface LoadDataOptions<T> {
    /**
     * load the data
     * @returns  promise
     */
    load: () => Promise<T>;
    /**
     *
     * @param data immeditately react to loaded content. Do not change state in this callback as it is alse called during hydration.
     */
    onLoad: (data: T) => void;
    /**
     * unique id to store the loaded data in memory and serialize it
     */
    globalDataId: string;
}

export type State = 'loading' | 'resolved' | 'rejected';

export interface DataState<T> {
    state: State;
    data?: T;
}

/**
 * load data. Works for ssr and triggers a rerender. serializes data to client.
 * @param props
 * @returns data state
 */
export function useLoadData<T>(props: LoadDataOptions<T>): DataState<T> {
    const {load, globalDataId, onLoad} = props;

    const memoryStore = useMemoryStore();
    const asyncSsrManager = useAsyncSsrManager();
    const logger = useLogger().incentive;
    const initialDataUpdated = React.useRef(false);
    const hydration = !useIsClientOnlyRendering();

    const initialData = memoryStore.getData(globalDataId);

    const [data, setData] = React.useState<DataState<T>>(
        initialData || {state: 'loading'}
    );

    useEffect(() => {
        if (hydration) {
            return;
        }
        const promise = load();
        promise
            .then(data => {
                onLoad(data);
                setData({state: 'resolved', data});
            })
            .catch(e => {
                logger.error('cannot load incentive', e);
                setData({state: 'rejected'});
            });
    }, [load, setData, logger, initialData, onLoad, hydration]);

    if (data.state === 'loading' && !isInBrowser()) {
        const promise = load();
        promise
            .then(data => {
                memoryStore.setData(globalDataId, {state: 'resolved', data});
                onLoad(data);
            })
            .catch(e => {
                logger.error('cannot load incentive', e);
                memoryStore.setData(globalDataId, {state: 'rejected'});
            });

        logger.debug('schedule rerender for loading incentive');
        asyncSsrManager.scheduleRerender(
            promise.catch(() => {
                // ignore to not crash ssr render
            })
        );
    }

    if (
        initialData &&
        !initialDataUpdated.current &&
        initialData.state === 'resolved' &&
        initialData?.data
    ) {
        onLoad(initialData?.data);
        initialDataUpdated.current = true;
    }

    return data;
}
